import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { Box, Container, Grid, Flex } from 'theme-ui';
import ProductImages from '~/components/Product/Images';
import ContentSection from '~/components/Content/Section';
import CollectionProductNav from '~/components/Collection/ProductNav';
import ImgLoader from '../ImgLoader';
import LiteCarousel from '../LiteCarousel';
import CarouselVideoPlayer from '../ImageCarousel/VideoPlayer';
import { useLocation } from '@reach/router';
import { GroupContent } from '../GroupContent';
import ViewedProductPixel from '~/scripts/NBCU/ViewedProductPixel';

import { CombinedMedia } from '~/utils/combine-media';

type ContextValues = {
  isKit: boolean;
};
const defaultValues: ContextValues = {
  isKit: false,
};

const PDPWrapperContext = createContext(defaultValues);

const isVideo = (
  item: Queries.ImageFragment | Queries.muxVideoFragmentFragment | null | undefined
): item is Queries.muxVideoFragmentFragment => {
  return (item as Queries.muxVideoFragmentFragment)?.video !== undefined;
};

type Props = {
  children: ReactNode;
  selectedVariant?: Queries.VariantDetailsFragmentFragment | null;
  product?: Queries.ProductPageFragmentFragment;
  bundle?: Queries.BundlePageFragment | Queries.KitPageFragmentFragment;
  variant?: Queries.VariantDetailsFragmentFragment | null;
  mediaArrayWithVideos: CombinedMedia['mediaArrayWithVideos'];
  mediaArrayWithThumbnails: CombinedMedia['mediaArrayWithThumbnails'];
  zoomedMediaArrayWithVideos?: CombinedMedia['mediaArrayWithVideos'];
  productCollection: Queries.ProductCollectionFragment[];
  badge?: Queries.BadgeFragment | null;
};

const PDPWrapper = ({
  children,
  selectedVariant,
  product,
  bundle,
  variant,
  mediaArrayWithVideos,
  mediaArrayWithThumbnails,
  zoomedMediaArrayWithVideos,
  productCollection,
  badge,
}: Props) => {
  const isAllCollection = product?.collection?.[0]?.slug === 'all';
  const isKitOrBundle = (product === null || product === undefined) && bundle !== null && bundle !== undefined;
  const isBundle = bundle?.__typename === 'ContentfulBundle';

  const { pathname } = useLocation();

  const returnValues = {
    isKit: isKitOrBundle,
  };
  useEffect(() => {
    // This is a hack to reinitialize the Yotpo widget after the page has loaded.
    // This is necessary because the Yotpo widget does not load properly after a page transition.
    if (window.yotpoWidgetsContainer) {
      window.yotpoWidgetsContainer.initWidgets();
    }
  }, []);

  return (
    <PDPWrapperContext.Provider value={returnValues}>
      <ViewedProductPixel />
      {/* @ts-ignore */}

      <Container
        variant="fullwidth"
        sx={{
          '--vertical-spacing': ['2.4rem', '3.2rem'],
        }}
      >
        {!isAllCollection && <CollectionProductNav collection={productCollection} />}
        <Flex sx={{ display: 'flex', flexDirection: 'column', gap: '9.4rem' }}>
          <Grid
            // Used to have customNavContainer variant but this was conflicting with the Grid component
            sx={{
              '--horizontal-inset': [0, '4rem'],
              gridTemplateColumns: ['1fr', 'repeat(2, 1fr)', '2fr 1fr'],
              maxWidth: 'calc(1600px - var(--horizontal-inset) * 2)',
              width: '100%',
              mx: 'auto',
              gap: [0, '4rem'],
              paddingTop: [0, '2.4rem'],
              px: 'var(--horizontal-inset)',
            }}
          >
            <Box
              sx={{
                display: ['block', 'none'],
                mb: '2.4rem',
                width: '100%',
                aspectRatio: '1/1',
                position: 'relative',
              }}
            >
              {/* @ts-ignore */}
              <LiteCarousel
                transitionSpeed={0.5}
                slides={mediaArrayWithVideos.map((item, index) =>
                  isVideo(item) ? (
                    // @ts-ignore
                    <CarouselVideoPlayer slideId={item.id} key={item.id} video={item} />
                  ) : (
                    <ImgLoader
                      key={item?.id}
                      image={item}
                      forwardSx={{ width: '100%', height: '100%' }}
                      badge={index === 0 ? badge : undefined}
                    />
                  )
                )}
              />
            </Box>
            <Box
              sx={{
                display: ['none', 'block'],
                width: '100%',
                position: 'relative',
              }}
            >
              <ProductImages
                images={mediaArrayWithThumbnails}
                zoomedImages={zoomedMediaArrayWithVideos ?? mediaArrayWithVideos}
                fromPdp={true}
                badge={badge}
              />
            </Box>
            {children}
          </Grid>
          {/* Showcase */}
          {isKitOrBundle && (
            <>
              {/* @ts-ignore */}
              <GroupContent group={bundle.productShowcase} />

              {bundle?.sections?.map((section) => {
                return (
                  <Flex key={section?.id} sx={{ gap: '5.8rem 0', mb: '2rem' }}>
                    {/* @ts-ignore */}
                    <ContentSection section={section} productSection={true} />
                  </Flex>
                );
              })}
            </>
          )}
          {!isKitOrBundle && !isBundle && (
            <>
              {/* @ts-ignore */}
              <GroupContent group={variant?.productShowcase} />

              {product?.sections?.map((section) => {
                return (
                  <Flex key={section?.id} sx={{ gap: '5.8rem 0', mb: '2rem' }}>
                    <ContentSection
                      // @ts-ignore
                      section={section}
                      productSection={true}
                      product={product}
                      selectedVariant={selectedVariant}
                      pathname={pathname}
                    />
                  </Flex>
                );
              })}
            </>
          )}
        </Flex>
      </Container>
    </PDPWrapperContext.Provider>
  );
};

export const usePDPWrapperValues = () => {
  return useContext(PDPWrapperContext) || {};
};

export default PDPWrapper;
